import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Modal } from "react-bootstrap";
import { useHistory } from "react-router";
import { FiBarChart2, FiList } from "react-icons/fi";
import SecoundaryHeader from "../Nav/SecoundaryHeader";
import { asyncWrap } from "../../utils/utils";

const fmt = new Intl.NumberFormat("en-IN");
const num = value => (Number.isFinite(Number(value)) ? Number(value) : 0);
const list = payload => payload?.data?.data || payload?.data || payload || [];
const schemeName = item => item.SchemeName || item.Comment || 'No Scheme Name';
const currentMonthKey = () => {
  const now = new Date();
  return now.getFullYear() * 12 + now.getMonth() + 1;
};
const isTillCurrentMonth = item =>
  num(item.Year) * 12 + num(item.MonthNumber) <= currentMonthKey();

const styles = {
  page: { background: "#f5f7fb", minHeight: "100vh", padding: "86px 12px 24px" },
  shell: { margin: "0 auto", maxWidth: 920 },
  card: {
    background: "#fff",
    border: "1px solid #e8edf5",
    borderRadius: 8,
    boxShadow: "0 8px 22px rgba(31, 41, 55, 0.06)",
    marginBottom: 14,
    padding: 14,
  },
  titleRow: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    marginBottom: 10,
  },
  h2: { color: "#111827", fontSize: 18, fontWeight: 800, margin: 0 },
  hint: { color: "#6b7280", fontSize: 12, fontWeight: 600, margin: "3px 0 0" },
  iconButton: {
    alignItems: "center",
    background: "#f1f5f9",
    border: "1px solid #dbe3ef",
    borderRadius: 8,
    color: "#334155",
    display: "flex",
    height: 38,
    justifyContent: "center",
    width: 38,
  },
  alert: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 8,
    color: "#9a3412",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 12,
    padding: 10,
  },
  loader: { alignItems: "center", display: "flex", justifyContent: "center", minHeight: 300 },
  monthGrid: { display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))" },
  monthCard: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10 },
  monthName: { color: "#111827", fontSize: 13, fontWeight: 800, marginBottom: 8 },
  metricRow: { display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, marginTop: 5 },
  metricLabel: { color: "#64748b", fontWeight: 700 },
  metricValue: { color: "#111827", fontWeight: 800, textAlign: "left" },
  schemeCard: {
    background: "#fff",
    border: "1px solid #e8edf5",
    borderRadius: 8,
    boxShadow: "0 8px 22px rgba(31, 41, 55, 0.06)",
    marginBottom: 10,
    padding: 13,
  },
  schemeTitle: { color: "#111827", fontSize: 15, fontWeight: 800, margin: "0 0 4px" },
  comment: { color: "#64748b", fontSize: 12, fontWeight: 600, margin: "0 0 10px" },
  schemeMetrics: { display: "grid", gap: 8, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  metricBox: { background: "#f8fafc", borderRadius: 8, padding: "9px 10px" },
};

const Analytics = ({ show, setShow }) => {
  const history = useHistory();
  const isOpen = show === undefined ? true : show;
  const [monthlyData, setMonthlyData] = useState([]);
  const [schemeData, setSchemeData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const closeAnalytics = () => (setShow ? setShow(false) : history.goBack());

  const fetchAnalytics = async () => {
    setLoading(true);
    setErrorMessage("");

    const [[monthErr, monthRes], [schemeErr, schemeRes]] = await Promise.all([
      asyncWrap(axios.get("/user-analytics-fy/earned-redeemed-scanned-point-info")),
      asyncWrap(axios.get("/user-analytics-fy/scheme-analytics-data")),
    ]);

    if (!monthRes && !schemeRes) {
      console.log(monthErr || schemeErr);
      setErrorMessage("Unable to load analytics right now.");
    } else if (!monthRes || !schemeRes) {
      console.log(monthErr || schemeErr);
      setErrorMessage("Some analytics data could not be loaded.");
    }

    const monthList = Array.isArray(list(monthRes?.data)) ? list(monthRes?.data) : [];
    setMonthlyData(monthList.filter(isTillCurrentMonth));
    setSchemeData(Array.isArray(list(schemeRes?.data)) ? list(schemeRes?.data) : []);
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) fetchAnalytics();
  }, [isOpen]);

  const monthLabels = useMemo(
    () => monthlyData.map(item => `${String(item.Month || "").slice(0, 3)} ${item.Year || ""}`),
    [monthlyData]
  );

  const monthSeries = useMemo(
    () => [
      { name: "Points Earned", type: "column", data: monthlyData.map(item => num(item["Points Earned"])) },
      { name: "Points Redeemed", type: "column", data: monthlyData.map(item => num(item["Points Redeemed"])) },
      { name: "Products Scanned", type: "line", data: monthlyData.map(item => num(item["Products Scanned"])) },
    ],
    [monthlyData]
  );

  const monthOptions = useMemo(
    () => ({
      chart: { fontFamily: "inherit", toolbar: { show: false } },
      colors: ["#2563eb", "#f97316", "#16a34a"],
      dataLabels: { enabled: false },
      grid: { borderColor: "#edf0f5", strokeDashArray: 4 },
      legend: { horizontalAlign: "left", position: "top" },
      plotOptions: { bar: { borderRadius: 5, columnWidth: "45%" } },
      stroke: { curve: "smooth", width: [0, 0, 3] },
      xaxis: {
        categories: monthLabels,
        labels: { rotate: -35, style: { colors: "#64748b", fontSize: "11px" } },
      },
      yaxis: {
        labels: { formatter: value => fmt.format(num(value)) },
      },
      tooltip: { y: { formatter: value => fmt.format(num(value)) } },
    }),
    [monthLabels]
  );

  return (
    <Modal fullscreen={true} show={isOpen} onHide={closeAnalytics}>
      <SecoundaryHeader text="Analytics" sideHide={true} onBack={closeAnalytics} />
      <Modal.Body style={styles.page}>
        <div style={styles.shell}>
          {loading ? (
            <div style={styles.loader}>
              <div id="quiz-loader"></div>
            </div>
          ) : (
            <>
              {errorMessage && <div style={styles.alert}>{errorMessage}</div>}

              <section style={styles.card}>
                <div style={styles.titleRow}>
                  <div>
                    <h2 style={styles.h2}>Month Wise Analytics</h2>
                    <p style={styles.hint}>Earned, redeemed and scanned month wise.</p>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      style={styles.iconButton}
                      type="button"
                      title={showTable ? "Show graph" : "Show month data"}
                      onClick={() => setShowTable(value => !value)}
                    >
                      {showTable ? <FiBarChart2 /> : <FiList />}
                    </button>
                    {/* <button
                      style={styles.iconButton}
                      type="button"
                      title="Refresh analytics"
                      onClick={fetchAnalytics}
                    >
                      <FiRefreshCw />
                    </button> */}
                  </div>
                </div>

                {!monthlyData.length ? (
                  <div style={{ ...styles.hint, padding: "34px 0", textAlign: "center" }}>
                    No month wise data found.
                  </div>
                ) : showTable ? (
                  <div style={styles.monthGrid}>
                    {monthlyData.map(item => (
                      <div style={styles.monthCard} key={`${item.Year}-${item.MonthNumber}`}>
                        <div style={styles.monthName}>{item.Month} {item.Year}</div>
                        <div style={styles.metricRow}>
                          <span style={styles.metricLabel}>Earned</span>
                          <span style={styles.metricValue}>{fmt.format(num(item["Points Earned"]))}</span>
                        </div>
                        <div style={styles.metricRow}>
                          <span style={styles.metricLabel}>Redeemed</span>
                          <span style={styles.metricValue}>{fmt.format(num(item["Points Redeemed"]))}</span>
                        </div>
                        <div style={styles.metricRow}>
                          <span style={styles.metricLabel}>Scanned</span>
                          <span style={styles.metricValue}>{fmt.format(num(item["Products Scanned"]))}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Chart options={monthOptions} series={monthSeries} type="line" height={360} />
                )}
              </section>

              <section>
                <div style={{ ...styles.titleRow, marginBottom: 8 }}>
                  <div>
                    <h2 style={styles.h2}>Scheme Analytics</h2>
                    <p style={styles.hint}>Scheme and comment wise data.</p>
                  </div>
                </div>
                {schemeData.length ? (
                  schemeData.map((item, index) => (
                    <article style={styles.schemeCard} key={`${schemeName(item)}-${item.Comment || index}`}>
                      <h3 style={styles.schemeTitle}>{schemeName(item)}</h3>
                      {item.Comment && <p style={styles.comment}>{item.Comment}</p>}
                      <div style={styles.schemeMetrics}>
                        <div style={styles.metricBox}>
                          <div style={styles.metricLabel}>Points Earned</div>
                          <div style={styles.metricValue}>{fmt.format(num(item.PointsEarned))}</div>
                        </div>
                        <div style={styles.metricBox}>
                          <div style={styles.metricLabel}>Success Products Scanned</div>
                          <div style={styles.metricValue}>{fmt.format(num(item.SuccessProductsScanned))}</div>
                        </div>
                      </div>
                    </article>
                  ))
                ) : (
                  <div style={styles.card}>
                    <p style={{ ...styles.hint, margin: 0, textAlign: "center" }}>No scheme data found.</p>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default Analytics;
